/**
 * 채점 모드 선택 컴포넌트
 *
 * 학생이 문제 풀이 방식을 선택하는 UI
 * - 게임 모드: 기존 방식 (문제를 하나씩 풀이)
 * - 사진 모드: Vision API로 답안지 일괄 채점
 *
 * 레트로 픽셀 스타일 UI
 */

import React from 'react';

/**
 * GradingModeSelector Props
 */
interface GradingModeSelectorProps {
  /** 게임 모드 선택 콜백 */
  onSelectGameMode: () => void;

  /** 사진 모드 선택 콜백 */
  onSelectPhotoMode: () => void;

  /** 비활성화 여부 (로딩 중 등) */
  disabled?: boolean;
}

/**
 * 채점 모드 선택 컴포넌트
 *
 * 두 개의 큰 버튼으로 게임 모드와 사진 모드를 선택
 *
 * @example
 * ```tsx
 * <GradingModeSelector
 *   onSelectGameMode={() => setPageMode('solving')}
 *   onSelectPhotoMode={() => setPageMode('photo_flow')}
 * />
 * ```
 */
export const GradingModeSelector: React.FC<GradingModeSelectorProps> = ({
  onSelectGameMode,
  onSelectPhotoMode,
  disabled = false,
}) => {
  return (
    <div className="space-y-4">
      {/* 제목 */}
      <h3 className="font-pixel text-sm text-center text-[#9bbc0f] mb-4">
        어떻게 풀래?
      </h3>

      {/* 모드 선택 버튼 그리드 */}
      <div className="grid grid-cols-1 gap-4">
        {/* 게임 모드 버튼 */}
        <button
          onClick={onSelectGameMode}
          disabled={disabled}
          className={`
            w-full p-6 rounded-lg
            bg-gray-800 hover:bg-gray-700
            border-4 border-[#4b5563]
            transition-all duration-150
            disabled:opacity-50 disabled:cursor-not-allowed
            active:translate-y-1
          `}
          style={{
            boxShadow: '0 6px 0 #374151, 0 8px 10px rgba(0,0,0,0.3)',
          }}
        >
          <div className="flex flex-col items-center space-y-3">
            {/* 아이콘 */}
            <span className="text-4xl">💬</span>

            {/* 제목 */}
            <span className="font-pixel text-lg text-white">
              채팅으로 채점
            </span>

            {/* 설명 */}
            <span className="font-pixel text-xs text-gray-400">
              채점봇과 대화하며 문제를 하나씩 풀어요!
            </span>
          </div>
        </button>

        {/* 사진 모드 버튼 */}
        <button
          onClick={onSelectPhotoMode}
          disabled={disabled}
          className={`
            w-full p-6 rounded-lg
            bg-[#0f380f] hover:bg-[#306230]
            border-4 border-[#9bbc0f]
            transition-all duration-150
            disabled:opacity-50 disabled:cursor-not-allowed
            active:translate-y-1
          `}
          style={{
            boxShadow: '0 6px 0 #0f380f, 0 8px 10px rgba(0,0,0,0.3)',
          }}
        >
          <div className="flex flex-col items-center space-y-3">
            {/* 아이콘 */}
            <span className="text-4xl">📷</span>

            {/* 제목 */}
            <span className="font-pixel text-lg text-[#9bbc0f]">
              사진으로 채점
            </span>

            {/* 설명 */}
            <span className="font-pixel text-xs text-[#9bbc0f]/70">
              답지양식에 답을 적어 사진을 찍으면 AI가 한번에 채점해요!
            </span>

            {/* 안내 메시지 */}
            <div className="mt-2 p-2 bg-yellow-900/40 border border-yellow-600/50 rounded">
              <p className="font-pixel text-xs text-yellow-400">
                ⚠️ 문제집을 직접 촬영하지 마세요!
              </p>
              <p className="font-pixel text-xs text-yellow-400/80 mt-1">
                📄 선생님께 답지양식 문서를 받아서 사용하세요
              </p>
            </div>
          </div>
        </button>
      </div>
    </div>
  );
};

GradingModeSelector.displayName = 'GradingModeSelector';
